import type { CityConfig } from "../city-template/types";

export const postFallsConfig: CityConfig = {
  city: "Post Falls",
  state: "Idaho",
  stateAbbr: "ID",
  slug: "post-falls",
  route: "/cities/post-falls",
  title: "Post Falls ID Passport — Expedited & Emergency Service | Fast Passport Center",
  metaDescription: "Expedited and emergency passport services in Post Falls, Idaho — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Post Falls, Coeur d'Alene, Hayden, Rathdrum, and the greater Kootenai County region.",
  h1: "Expedited Passport Services in Post Falls, ID",
  heroSubtitle: "Planning an international trip from Spokane International Airport? Business travel to Canada just 90 minutes away? Post Falls residents — from Prairie Falls families to Spokane River outdoor enthusiasts to Coeur d'Alene-area professionals — trust us for fast, reliable passport processing. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20scenic%20view%20of%20Post%20Falls%20Idaho%20along%20the%20Spokane%20River%20with%20lush%20green%20forested%20hills%20in%20the%20background%2C%20golden%20hour%20sunlight%20reflecting%20on%20the%20water%2C%20peaceful%20Pacific%20Northwest%20landscape%20with%20evergreen%20trees%20and%20mountains%20in%20the%20distance%2C%20editorial%20nature%20photography%20with%20warm%20tones%20and%20natural%20composition&width=1800&height=700&seq=post-falls-hero-2026&orientation=landscape",
  heroImageAlt: "Post Falls Idaho Spokane River scenic view at golden hour",
  geoPosition: "47.7145;-116.9519",
  geoPlacename: "Post Falls, Idaho",
  lastModified: "2026-06-07",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "3015 S Hanford St, Seattle, WA 98134",
    distance: "Closest regional agency — approximately 290 miles via I-90, appointments required",
  },
  nearbyCitiesZips: [
    { name: "Post Falls", zips: "83854, 83877" },
    { name: "Coeur d'Alene", zips: "83814, 83815, 83816" },
    { name: "Hayden", zips: "83835" },
    { name: "Rathdrum", zips: "83858" },
    { name: "Spirit Lake", zips: "83869" },
    { name: "Spokane", zips: "99201–99220" },
    { name: "Spokane Valley", zips: "99206, 99212, 99216" },
  ],
  benefits: [
    { icon: "ri-flashlight-line", title: "24-Hour North Idaho Emergency Processing", desc: "Reserved courier access — as fast as 24 hours for Post Falls and Kootenai County travelers with immediate departures from Spokane International Airport." },
    { icon: "ri-flight-takeoff-line", title: "GEG Airport Expertise", desc: "Spokane International Airport is just 35 minutes away. We know airline schedules, seasonal routes, and last-minute booking dynamics for North Idaho travelers." },
    { icon: "ri-ship-line", title: "Cross-Border Canada Travel Ready", desc: "Post Falls is just 90 minutes from the Canadian border. We handle expedited needs for British Columbia business trips, Vancouver getaways, and cross-border family visits." },
    { icon: "ri-home-heart-line", title: "Kootenai County Family Focus", desc: "From first-time passports for Prairie Falls kids to renewals for Rathdrum retirees heading south for winter — we guide North Idaho families through every step." },
  ],
  internalLinks: [
    { name: "Fast passport services in Coeur d'Alene, ID", route: "/cities/coeur-dalene" },
    { name: "Expedited services in Boise, ID", route: "/cities/boise" },
    { name: "Idaho passport services overview", route: "/states/idaho" },
  ],
  fpcOfficeAddress: "3015 S Hanford St, Seattle, WA 98134",
  fpcOfficeNote: "Fast Passport Center serves Post Falls via secure registered courier with direct access to the Seattle Passport Agency. Our registered couriers hand-deliver your documents — meaning Post Falls residents get faster results without driving nearly 300 miles to Seattle. Ship from any UPS Store, FedEx Office, or post office in Kootenai County.",
  fpcOfficeDistance: "Serving Post Falls, Coeur d'Alene, Hayden, Rathdrum, Spirit Lake, and all Kootenai County communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Post Falls and greater Kootenai County. From the Spokane River corridor to the Rathdrum Prairie, from Hayden Lake to the Coeur d'Alene National Forest — we serve all northern Idaho communities. Our courier service eliminates the need to drive to Seattle for a passport agency appointment.",
  nearbyCitiesIntro: "We serve Post Falls and all Kootenai County communities including Coeur d'Alene, Hayden, Rathdrum, Spirit Lake, and nearby Spokane Valley. We also serve travelers departing from Spokane International Airport (GEG) who need emergency passports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Post Falls as simple as possible. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Post Falls travelers start online and ship documents overnight — no office visit required.",
  mistakesIntro: "North Idaho travelers encounter unique passport issues — from last-minute Canada border crossings to GEG international flight bookings with expired documents. Here are the most common passport mistakes we see from this area.",
  testimonialsIntro: "Post Falls and Kootenai County travelers — from Prairie Falls families to Rathdrum professionals to Hayden Lake retirees — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Post Falls, ID? Here are the most common ones we hear from North Idaho travelers.",
  statePageRoute: "/states/idaho",
  statePageName: "Idaho",
  servicesIntro: "Whether you need a renewal before a Spokane-to-Phoenix flight connecting internationally, an emergency passport for a last-minute Canada border crossing, or a first-time passport for your Kootenai County family vacation abroad — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://itd.idaho.gov/dmv/",
  dmvLabel: "Idaho Transportation Department — DMV Services",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to travel to Seattle (290 miles)"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates", "No need to drive to Seattle"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned", "Daily status updates"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent", "Updates only by physical mail", "Cannot upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "GEG flight to Phoenix with an international connection tomorrow? Family emergency in Canada this week? Our emergency courier service submits via reserved agency slots — as fast as 24 hours for Post Falls travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Post Falls residents renew fast — no agency visit required for most renewals. Perfect for Kootenai County families and professionals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Post Falls residents through every step with clear instructions for the nearest acceptance facility.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your GEG international connection is not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists so your family trip stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. Perfect for frequent British Columbia travelers.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: "BC Travel Essential",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Post Falls ID", description: "Urgent passport processing for Post Falls Idaho travelers with immediate departure needs from Spokane International Airport." },
    { name: "Expedited Passport Renewal Post Falls ID", description: "Fast passport renewal for Post Falls Idaho residents with a previously-issued passport." },
    { name: "First-Time Passport Post Falls ID", description: "New passport application services for Post Falls Idaho residents applying for their first passport." },
    { name: "Child Passport Post Falls ID", description: "Minor passport services for Post Falls Idaho families with children under 16." },
    { name: "Name Change Passport Post Falls ID", description: "Passport name correction after marriage, divorce, or legal name changes for Post Falls residents." },
    { name: "Passport Card Post Falls ID", description: "Passport card services for land and sea crossings to Canada from Post Falls Idaho." },
  ],
  testimonials: [
    {
      name: "The Andersons",
      location: "Post Falls",
      rating: 5,
      text: "We booked a last-minute cruise from Vancouver and realized our kids' passports expired. Fast Passport Center got all three passports back in 6 days. They caught a photo issue we never would have noticed.",
      service: "Child Passport Renewal",
    },
    {
      name: "Mike R.",
      location: "Hayden",
      rating: 5,
      text: "Needed a passport card for frequent trips to British Columbia for work. They processed it faster than I thought possible and the document review saved me from a rejection.",
      service: "Passport Card",
    },
    {
      name: "Sarah T.",
      location: "Rathdrum",
      rating: 5,
      text: "My daughter got accepted to a study abroad program in Spain with only 3 weeks notice. No passport. Fast Passport Center walked us through everything and the passport arrived in 5 business days.",
      service: "New Passport",
    },
    {
      name: "Jim W.",
      location: "Post Falls",
      rating: 5,
      text: "Realized my passport expired 48 hours before a flight to Mexico. They got me an emergency renewal and I made my flight. Incredible service for North Idaho.",
      service: "Emergency Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "postfalls-1",
      name: "Post Falls Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "405 N Spokane St",
      city: "Post Falls",
      state: "ID",
      zip: "83854",
      phone: "(208) 773-4614",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Downtown Post Falls",
      website: "https://tools.usps.com/find-location.htm?location=1377561",
      websiteLabel: "USPS Location",
      notes: "Primary passport acceptance facility in Post Falls. Passport photos available. Appointment recommended — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "postfalls-2",
      name: "Coeur d'Alene Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "111 N 7th St",
      city: "Coeur d'Alene",
      state: "ID",
      zip: "83814",
      phone: "(208) 664-2484",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Coeur d'Alene — 9 miles east",
      website: "https://tools.usps.com/find-location.htm?location=1377511",
      websiteLabel: "USPS Location",
      notes: "Largest acceptance facility in Kootenai County. Photo services available. Walk-in hours limited — appointment recommended during summer tourist season.",
      icon: "ri-mail-line",
    },
    {
      id: "postfalls-3",
      name: "Kootenai County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "451 N Government Way",
      city: "Coeur d'Alene",
      state: "ID",
      zip: "83814",
      phone: "(208) 446-1190",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      distance: "Government Way — 9 miles east",
      website: "https://www.kcgov.us/254/Clerk",
      websiteLabel: "Kootenai County Clerk",
      notes: "County clerk acceptance services in Coeur d'Alene. Serves all Kootenai County residents including Post Falls, Hayden, and Rathdrum. No photo services on-site.",
      icon: "ri-government-line",
    },
    {
      id: "postfalls-4",
      name: "Hayden Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 W Prairie Ave",
      city: "Hayden",
      state: "ID",
      zip: "83835",
      phone: "(208) 772-3631",
      hours: "Mon–Fri 9:00 AM – 4:00 PM",
      distance: "Hayden — 5 miles north",
      website: "https://tools.usps.com/find-location.htm?location=1377531",
      websiteLabel: "USPS Location",
      notes: "Smaller acceptance facility serving Hayden and northern Kootenai County. Fewer appointment slots — book well in advance during summer.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — typically booked 2+ weeks out, walk-ins not accepted", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — drive 290 miles to Seattle Passport Agency", fpc: "Most North Idaho travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel with proof of departure", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "North Idaho Guidance", agency: "Generic — no GEG airport or Canada border expertise", fpc: "Full North Idaho/Spokane Airport/Canada border knowledge built in" },
  ],
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Assuming Canada Doesn't Need a Passport",
      description: "Post Falls is just 90 minutes from the border, but Canada requires a valid passport or passport card. Many North Idaho residents assume a driver's license is enough — it is not. And REAL ID is not a passport substitute.",
    },
    {
      icon: "ri-error-warning-line",
      title: "GEG Flight Passport Oversights",
      description: "Travelers often book Phoenix-to-Cancun or Seattle-to-Europe connecting flights through Spokane without realizing their passport must be valid for the entire trip. GEG is a domestic airport but connects to dozens of international destinations.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations",
      description: "Many North Idaho pharmacies produce photos that fail State Department standards — wrong background color, glare on glasses, head too small. We catch this in review before it causes a rejection.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Missing Parental Consent for Minors",
      description: "Kootenai County families frequently overlook the DS-3053 consent form when one parent is unavailable. This is an automatic rejection — we guide you through it correctly.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Not Accounting for 6-Month Rule",
      description: "North Idaho travelers booking cruises or Europe trips often do not know their passport must have 6 months remaining beyond the return date. We flag this immediately.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home or office." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Post Falls clients ship from any UPS Store or FedEx Office in Kootenai County." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you do not have to drive 290 miles to Seattle." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ],
  faqs: [
    { q: "How fast can I get a passport in Post Falls?", a: "We offer Post Falls-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Is there a passport office in Post Falls?", a: "The Post Falls Post Office at 405 N Spokane St serves as a passport acceptance facility for first-time applications. The Coeur d'Alene Main Post Office and Kootenai County Clerk also provide acceptance services. For expedited processing beyond the standard timeline, our registered courier service is the fastest option available." },
    { q: "Do I need a passport to go to Canada from Post Falls?", a: "Yes. Despite being only 90 minutes from the Canadian border, you need either a valid U.S. passport book or a passport card for land crossings. A driver's license alone is not sufficient, and REAL ID is not a passport substitute." },
    { q: "Where is the closest passport agency to Post Falls?", a: "The Seattle Passport Agency at 3015 S Hanford St is approximately 290 miles away. It requires appointments and serves life-or-death emergencies only. For all other expedited needs, our courier channel is faster and eliminates the drive." },
    { q: "Can you help with passports for flights from Spokane Airport?", a: "Yes. Spokane International Airport (GEG) is just 35 minutes from Post Falls. We regularly assist Kootenai County travelers with expedited passports for domestic-to-international connections through GEG." },
    { q: "What documents do Post Falls applicants need?", a: "New passport: certified birth certificate, government-issued photo ID, DS-11 form, and passport photo. Renewal: DS-82 form, current passport, and passport photo. Child passport: DS-11, birth certificate, both parents or notarized consent, and photos. We provide a Post Falls-specific checklist." },
    { q: "Where can I get passport photos in Post Falls?", a: "The Post Falls Post Office, Coeur d'Alene Post Office, Walgreens on Seltice Way, and Staples in Coeur d'Alene all offer passport photo services. We recommend confirming photo standards compliance before visiting." },
    { q: "What is the best price for expedited passport service in Post Falls?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers." },
    { q: "How do I check my passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Post Falls to final return shipping typically matches your chosen speed tier." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Post Falls Residents",
    intro: "Post Falls' unique location — minutes from Spokane, close to the Canadian border — creates distinct passport timing challenges. Here's what North Idaho travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "GEG — Spokane International Airport",
        subtitle: "Primary commercial airport for the Inland Northwest, just 35 minutes away",
        description: "Spokane International Airport serves the entire Inland Northwest with connections to major U.S. hubs — Seattle, Denver, Phoenix, Minneapolis — where travelers connect to international flights worldwide. Many Post Falls residents book domestic flights through GEG that connect to international destinations, often not checking passport validity until check-in. During peak summer season (June–August), the airport is especially busy with tourists and cruisers heading to Alaska, Canada, and beyond.",
        tip: "If you are connecting through Seattle, Denver, or any hub to an international destination, your passport must be valid at your GEG departure — not just at your international connection point.",
      },
      {
        icon: "ri-ship-line",
        title: "Canada Border Crossings",
        subtitle: "Eastport, Porthill, and other northern Idaho border crossings",
        description: "Post Falls is just 90 minutes from the Canadian border. Many North Idaho families regularly cross into British Columbia — for vacations in the Canadian Rockies, ski trips to Fernie and Kimberley, fishing trips on Kootenay Lake, or weekend getaways to Nelson and Kaslo. Business travelers frequently cross the border for meetings in Cranbrook, Calgary, and Vancouver. The convenience of proximity sometimes leads to passport complacency — travelers assume their documents are valid without checking.",
        tip: "Check your passport at least 3 months before any Canada trip. Even a weekend drive to Cranbrook requires valid documentation. Passport cards are a convenient, lower-cost option for frequent land crossings.",
      },
      {
        icon: "ri-anchor-line",
        title: "Alaska Cruises & Seasonal Travel",
        subtitle: "Popular summer cruise connections through Seattle and Vancouver",
        description: "Summer is prime Alaska cruise season, and many North Idaho families book cruises departing from Seattle or Vancouver. These trips typically are booked 3–6 months in advance, but many families realize their passports are expired or about to expire only when completing cruise check-in paperwork. Cruise lines have no flexibility on documentation — no valid passport, no boarding, no refund.",
        tip: "Cruise lines are strict: your passport must be valid for at least 6 months beyond your return date. Check your entire family's passports the moment you book your cruise.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "Post Falls & Cross-Border Canada Travel",
      description: "Post Falls and Kootenai County have deep social and economic ties to British Columbia. From family reunions in the Okanagan Valley to business meetings in Calgary, from fishing trips on Kootenay Lake to ski weekends at Fernie Alpine Resort — cross-border travel is part of life in North Idaho. This proximity means passport urgencies arise frequently and unexpectedly.",
    },
  },
  destinations: {
    title: "Where Post Falls Travelers Go — And Why Passports Get Urgent",
    intro: "Post Falls residents travel internationally for business, family, and recreation. Canada is by far the most common destination, but cruises, Europe trips, and Mexican getaways create regular passport demand.",
    stats: [
      { stat: "60%", label: "of Post Falls expedited requests involve Canada travel — business, family visits, ski vacations" },
      { stat: "2–4 Weeks", label: "average lead time North Idaho travelers have from booking to international departure" },
      { stat: "Jun–Sep", label: "peak season when Alaska cruises, Canada road trips, and summer Europe travel converge" },
      { stat: "48 Hours", label: "shortest turnaround we have accommodated for a Post Falls resident heading to BC" },
    ],
    destinations: [
      { name: "Canada", context: "British Columbia road trips, Canadian Rockies skiing, Vancouver business, family visits in Okanagan", urgency: "Cross-border travel bookings often made 1–2 weeks out", icon: "ri-map-pin-2-line" },
      { name: "Mexico", context: "GEG-Phoenix-or-Denver connections to Cancun, Puerto Vallarta, Cabo San Lucas resorts", urgency: "Spring break and winter getaway bookings peak December–February", icon: "ri-map-pin-2-line" },
      { name: "Europe", context: "GEG connections through Seattle or Denver to London, Paris, Frankfurt, Rome", urgency: "Summer vacation bookings with fixed tour departure dates", icon: "ri-map-pin-2-line" },
      { name: "Alaska Cruises", context: "Inside Passage cruises departing Seattle and Vancouver, popular summer choice", urgency: "Cruise line documentation deadlines are strict and non-negotiable", icon: "ri-map-pin-2-line" },
      { name: "Caribbean", context: "All-inclusive resort getaways, connecting through major hubs from GEG", urgency: "Winter escape bookings 3–4 weeks out, families discover passport issues at check-in", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other North Idaho Service Areas",
    crossLinksDesc: "We serve travelers across northern Idaho and eastern Washington with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Coeur d'Alene", route: "/cities/coeur-dalene" },
      { name: "Boise", route: "/cities/boise" },
    ],
  },
  photoLocations: [
    { name: "Post Falls Post Office", address: "405 N Spokane St, Post Falls, ID 83854", hours: "Mon–Fri 9 AM – 4 PM" },
    { name: "Walgreens — Post Falls", address: "700 N Seltice Way, Post Falls, ID 83854", hours: "Daily 7 AM – 11 PM" },
    { name: "Staples — Coeur d'Alene", address: "220 W Ironwood Dr, Coeur d'Alene, ID 83814", hours: "Mon–Fri 8 AM – 8 PM, Sat 9 AM – 6 PM" },
    { name: "The UPS Store — Hayden", address: "8177 N Cornerstone Dr, Hayden, ID 83835", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Kootenai County DMV — Post Falls",
      address: "317 W Mullan Ave, Post Falls, ID 83854",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Driver licenses, REAL ID, vehicle registration. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kootenai County DMV — Coeur d'Alene",
      address: "451 N Government Way, Coeur d'Alene, ID 83814",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Full-service DMV. REAL ID available. Appointment strongly recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "GEG",
      name: "Spokane International Airport",
      distance: "About 35 minutes from Post Falls",
      address: "9000 W Airport Dr, Spokane, WA 99224",
      note: "Primary commercial airport for the Inland Northwest. Major airlines connect through Seattle, Denver, Phoenix, Minneapolis, and Salt Lake City to international destinations worldwide.",
      flightUrl: "https://www.spokaneairports.net/",
      flightLabel: "GEG Airport Website",
      featured: true,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from Idaho Vital Records. Online, mail, or in-person options available for Kootenai County.",
      url: "https://healthandwelfare.idaho.gov/services-programs/birth-death-marriage-divorce",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through Idaho Vital Records. Required for estate and legal matters in Kootenai County.",
      url: "https://healthandwelfare.idaho.gov/services-programs/birth-death-marriage-divorce",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Kootenai County Clerk. Certified copies available for name changes and legal purposes.",
      url: "https://www.kcgov.us/254/Clerk",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Kootenai County District Court. Required for name change passport corrections.",
      url: "https://www.kcgov.us/244/District-Court",
    },
  ],
};